import { useMutation } from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnableFlightTrackerLayer } from 'features/mapping/hooks/useEnableFlightTrackerLayer';
import { registerDeviceWithWatchDuty } from 'shared/device';
import { logUnexpectedErrorAndNotifyUser } from 'shared/handleUnexpectedError';
import { VerifyPaymentData } from 'shared/types';
import { promptUserToRateApp } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';

const verifyPayment = async (data: {
  clientToken: string;
}): Promise<VerifyPaymentData> => {
  const response = await API.post<
    { clientToken: string },
    AxiosResponse<VerifyPaymentData>
  >('recurly_integration/payment_verification/', data);
  return response.data;
};

type UseHandlePaymentClientTokenProps = {
  clientToken?: string;
};

type UseHandlePaymentClientToken = {
  isPending: boolean;
};

export const useHandlePaymentClientToken = (
  props: UseHandlePaymentClientTokenProps,
): UseHandlePaymentClientToken => {
  const { clientToken } = props;
  const { loginSuccess } = useAuthState();
  const { enableFlightTrackerLayerForMember } = useEnableFlightTrackerLayer();
  const { t } = useTranslation();

  const { isPending, mutate } = useMutation({
    mutationFn: verifyPayment,
    onSuccess: async (data) => {
      loginSuccess(data.key, data.user);
      enableFlightTrackerLayerForMember(data.user);

      // associate user with token
      registerDeviceWithWatchDuty({ user: data.user });
      await promptUserToRateApp();
    },
    onError: (error) => {
      logUnexpectedErrorAndNotifyUser(t, { error });
    },
  });

  useEffect(() => {
    if (clientToken) {
      mutate({ clientToken });
    }
  }, [clientToken, mutate]);

  return { isPending };
};
